import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  X, Calendar, Clock, User, MapPin, Search,
  CheckCircle, AlertTriangle, Star, Car, Award, Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function CreateShiftModal({ initialDate, onClose, onSuccess }) {
  // Form state
  const [clientId, setClientId] = useState("");
  const [visitDate, setVisitDate] = useState(initialDate?.date || "");
  const [startTime, setStartTime] = useState(initialDate?.startTime || "09:00");
  const [endTime, setEndTime] = useState(initialDate?.endTime || "13:00");
  const [caregiverId, setCaregiverId] = useState("");
  const [services, setServices] = useState([]);

  // Data state
  const [clients, setClients] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [creating, setCreating] = useState(false);

  // Load clients
  useEffect(() => {
    const loadClients = async () => {
      try {
        const { data } = await api.get("/scheduling/calendar/resources?resource_type=clients");
        setClients(data.resources);
      } catch (err) {
        console.error("Failed to load clients:", err);
      }
    };
    loadClients();
  }, []);

  // Load recommendations when client/date/time selected
  useEffect(() => {
    if (clientId && visitDate && startTime && endTime) {
      loadRecommendations();
    }
  }, [clientId, visitDate, startTime, endTime]);

  const loadRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const { data } = await api.post("/scheduling/recommendations", {
        client_id: clientId,
        visit_date: visitDate,
        start_time: startTime,
        end_time: endTime,
        required_services: services,
        limit: 10,
      });
      setRecommendations(data.recommendations);
    } catch (err) {
      console.error("Failed to load recommendations:", err);
    } finally {
      setLoadingRecs(false);
    }
  };

  const handleCreate = async () => {
    if (!clientId || !visitDate || !startTime || !endTime) {
      alert("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      await api.post("/scheduling/visits", {
        client_id: clientId,
        caregiver_id: caregiverId || null,
        visit_date: visitDate,
        start_time: startTime,
        end_time: endTime,
        authorized_services: services,
        shift_type: caregiverId ? "regular" : "open",
      });
      onSuccess();
    } catch (err) {
      console.error("Failed to create shift:", err);
      alert(err.response?.data?.detail || "Failed to create shift");
    } finally {
      setCreating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-600 bg-emerald-50";
    if (score >= 60) return "text-blue-600 bg-blue-50";
    if (score >= 40) return "text-amber-600 bg-amber-50";
    return "text-gray-600 bg-gray-50";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold">Create New Shift</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left: Shift Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Shift Details</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client *
                </label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <Input
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time *
                  </label>
                  <Input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {caregiverId ? (
                    <span className="text-emerald-600 font-medium">
                      ✓ Caregiver selected
                    </span>
                  ) : (
                    <span>
                      No caregiver selected. This will create an <strong>Open Shift</strong>.
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Right: Recommendations */}
            <div className="border-l border-gray-200 pl-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  Recommended Caregivers
                </h3>
                {loadingRecs && (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                )}
              </div>

              {!clientId ? (
                <p className="text-sm text-gray-500">
                  Select a client to see recommendations
                </p>
              ) : recommendations.length === 0 && !loadingRecs ? (
                <p className="text-sm text-gray-500">
                  No available caregivers found
                </p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {recommendations.map((rec) => (
                    <button
                      key={rec.caregiver_id}
                      onClick={() => setCaregiverId(rec.caregiver_id)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        caregiverId === rec.caregiver_id
                          ? "border-[#06372B] bg-[#06372B]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium">{rec.name}</div>
                            {rec.warnings?.length > 0 && (
                              <div className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {rec.warnings[0]}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-sm font-semibold ${getScoreColor(rec.match_score)}`}>
                          {rec.match_score}%
                        </div>
                      </div>

                      {/* Ranking factors */}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {rec.continuity_info?.previous_visits > 0 && (
                          <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            {rec.continuity_info.previous_visits} visits
                          </span>
                        )}
                        {rec.travel_info?.distance_miles && (
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            {rec.travel_info.distance_miles} mi
                          </span>
                        )}
                        {rec.ranking_factors?.credentials?.reason && (
                          <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !clientId || !visitDate}
            className="bg-[#06372B] hover:bg-[#0a4a3a]"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : caregiverId ? (
              "Create Assigned Shift"
            ) : (
              "Create Open Shift"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
